import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/lib/notify";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { LifeBuoy, Plus, MessageSquare, Clock, CheckCircle2, AlertCircle, Send, ArrowLeft } from "lucide-react";
import { getAccessToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["General Inquiry", "Document Request", "Transaction Query", "Technical Issue"];

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  in_progress: { label: "In Progress", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  resolved: { label: "Resolved", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  closed: { label: "Closed", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" },
};

export default function InvestorSupport() {
  const { user } = useAuth();
  const myRole = user?.role || "investor";
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({ category: "", subject: "", message: "" });
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Small delay to ensure token is stored after login redirect
    const timer = setTimeout(fetchTickets, 100);
    return () => clearTimeout(timer);
  }, []);

  const fetchTickets = async () => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(res.data);
    } catch (err) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!newTicket.category || !newTicket.subject || !newTicket.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const token = getAccessToken();
      await axios.post(`${API}/api/support/tickets`, newTicket, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ticket created successfully");
      setShowCreate(false);
      setNewTicket({ category: "", subject: "", message: "" });
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const viewTicket = async (ticketId) => {
    try {
      const token = getAccessToken();
      const res = await axios.get(`${API}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedTicket(res.data);
    } catch (err) {
      toast.error("Failed to load ticket details");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSubmitting(true);
    try {
      const token = getAccessToken();
      await axios.post(`${API}/api/support/tickets/${selectedTicket.id}/reply`, { message: reply }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Reply sent");
      setReply("");
      viewTicket(selectedTicket.id);
      fetchTickets();
    } catch (err) {
      toast.error("Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Ticket Detail View
  if (selectedTicket) {
    const status = STATUS_CONFIG[selectedTicket.status] || STATUS_CONFIG.open;
    return (
      <div className="space-y-6" data-testid="ticket-detail-view">
        <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)} className="gap-2" data-testid="back-to-tickets">
          <ArrowLeft className="w-4 h-4" /> Back to Tickets
        </Button>

        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-xl" data-testid="ticket-subject">{selectedTicket.subject}</CardTitle>
                <CardDescription className="mt-1">
                  {selectedTicket.category} • Created {formatDate(selectedTicket.created_at)}
                </CardDescription>
              </div>
              <Badge className={`${status.color} border`} data-testid="ticket-status">{status.label}</Badge>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {selectedTicket.messages?.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`p-4 rounded-lg ${msg.sender_role === myRole ? "bg-primary/10 ml-8" : "bg-secondary mr-8"}`}
                    data-testid={`message-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">
                        {msg.sender_name}
                        {msg.sender_role !== myRole && (
                          <Badge variant="outline" className="ml-2 text-[10px]">{msg.sender_role === "super_admin" ? "Super Admin" : msg.sender_role === "admin" ? "Fund Manager" : msg.sender_role?.replace("_", " ")}</Badge>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground">{formatDate(msg.timestamp)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {selectedTicket.status !== "closed" && selectedTicket.status !== "resolved" && (
              <div className="mt-4 flex gap-2">
                <Textarea
                  placeholder="Type your reply..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="reply-input"
                />
                <Button onClick={sendReply} disabled={submitting || !reply.trim()} className="self-end" data-testid="send-reply-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
            {(selectedTicket.status === "resolved" || selectedTicket.status === "closed") && (
              <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50">
                <p className="text-sm text-muted-foreground">This ticket is {selectedTicket.status}. You can reopen it if needed.</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="border border-border text-xs"
                  onClick={async () => {
                    try {
                      const token = getAccessToken();
                      await axios.put(`${API}/api/support/tickets/${selectedTicket.id}/status?status=open`, {}, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      toast.success("Ticket reopened");
                      fetchTickets();
                      setSelectedTicket(prev => prev ? { ...prev, status: "open" } : prev);
                    } catch (err) { toast.error(err.response?.data?.detail || "Failed to reopen"); }
                  }}
                  data-testid="reopen-ticket-btn"
                >
                  Reopen Ticket
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tickets List View
  return (
    <div className="space-y-6" data-testid="investor-support-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <LifeBuoy className="w-7 h-7 text-primary" />
            Support
          </h1>
          <p className="text-muted-foreground mt-1">Get help with your account and investments</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="create-ticket-btn">
          <Plus className="w-4 h-4" /> New Ticket
        </Button>
      </div>

      {tickets.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No support tickets</h3>
            <p className="text-muted-foreground mb-4">Need help? Create a support ticket and we'll get back to you.</p>
            <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="create-first-ticket-btn">
              <Plus className="w-4 h-4" /> Create Ticket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => {
            const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
            return (
              <Card
                key={ticket.id}
                className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => viewTicket(ticket.id)}
                data-testid={`ticket-${ticket.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-medium truncate">{ticket.subject}</h3>
                        <Badge className={`${status.color} border text-[10px]`}>{status.label}</Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{ticket.category}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(ticket.updated_at)}
                        </span>
                        <span>•</span>
                        <span>{ticket.messages?.length || 1} message{ticket.messages?.length !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <MessageSquare className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[500px]" data-testid="create-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>Describe your issue and we'll get back to you as soon as possible.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Category</label>
              <Select value={newTicket.category} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                <SelectTrigger data-testid="ticket-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Subject</label>
              <Input
                placeholder="Brief summary of your issue"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                data-testid="ticket-subject-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Message</label>
              <Textarea
                placeholder="Describe your issue in detail..."
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                className="min-h-[120px]"
                data-testid="ticket-message-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={createTicket} disabled={submitting} data-testid="submit-ticket-btn">
                {submitting ? "Creating..." : "Create Ticket"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
